import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CacheProvider } from './context/CacheContext';
import PageTransition from './components/PageTransition';

// Pages
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import PatientList from './pages/PatientList';
import PatientDetail from './pages/PatientDetail';
import PriorAuthorizationList from './pages/PriorAuthorizationList';
import PractitionerList from './pages/PractitionerList';
import AdminOverview from './pages/AdminOverview';
import RaisePAForm from './pages/RaisePAForm';
import ProviderPAList from './pages/ProviderPAList';
import RequestList from './pages/RequestList';

// Payer pages
import PendingAuthorizations from './pages/payer/PendingAuthorizations';
import ReviewedAuthorizations from './pages/payer/ReviewedAuthorizations';
import ReviewAuthorization from './pages/payer/ReviewAuthorization';
import EPAManagement from './pages/payer/EPAManagement';


// CSS
import './App.css';
import './styles/global-styles.css';
import './App.css';

// Protected route component
interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  allowedRoles = [] 
}) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles.length > 0 && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <CacheProvider>
      <AuthProvider>
        <Router>
          <PageTransition />
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          
          {/* Protected routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/patients" 
            element={
              <ProtectedRoute allowedRoles={['provider', 'superuser']}>
                <PatientList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/requests" 
            element={
              <ProtectedRoute allowedRoles={['provider', 'superuser']}>
                <RequestList />
              </ProtectedRoute>
            } 
          />

          
          <Route 
            path="/patients/:patientId" 
            element={
              <ProtectedRoute>
                <PatientDetail />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/prior-authorizations" 
            element={
              <ProtectedRoute allowedRoles={['superuser']}>
                <PriorAuthorizationList />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/prior-authorizations/:patientId" 
            element={
              <ProtectedRoute>
                <PriorAuthorizationList />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/practitioners" 
            element={
              <ProtectedRoute allowedRoles={['superuser']}>
                <PractitionerList />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/admin-overview" 
            element={
              <ProtectedRoute allowedRoles={['superuser']}>
                <AdminOverview />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/raise-pa/:patientId" 
            element={
              <ProtectedRoute allowedRoles={['provider', 'superuser']}>
                <RaisePAForm />
              </ProtectedRoute>
            } 
          />
          
          <Route 
            path="/provider/pa-status" 
            element={
              <ProtectedRoute allowedRoles={['provider']}>
                <ProviderPAList />
              </ProtectedRoute>
            } 
          />
          
          {/* Payer routes */}
          <Route
            path="/payer/pending-authorizations"
            element={
              <ProtectedRoute allowedRoles={['payer']}>
                <PendingAuthorizations />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/payer/reviewed-authorizations"
            element={
              <ProtectedRoute allowedRoles={['payer']}>
                <ReviewedAuthorizations />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/payer/review-authorization/:authId"
            element={
              <ProtectedRoute allowedRoles={['payer']}>
                <ReviewAuthorization />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/payer/epa-management"
            element={
              <ProtectedRoute allowedRoles={['payer']}>
                <EPAManagement />
              </ProtectedRoute>
            }
          />
          
          {/* Redirect to landing page if no route matches */}
          <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
      </AuthProvider>
    </CacheProvider>
  );
};

export default App;
